/**
 * Permit report card — one project's reviews still with an agency, measured against each agency's
 * OWN published review time, for the end of every email we send a plan checker (Matt, 2026-11-03:
 * "a report card for the project ... at the end of every email we send to a plan checker").
 * The pressure is meant for the City: the numbers are the City's own target and our own log, in
 * calendar days. Never the AB 253 clock or any talk of a private plan checker
 * (docs/PLAN_CHECK_PERFORMANCE.md: the clock is team only).
 *
 * Per review still with the agency (not approved, submitted at least once):
 *   counting from: the review clock's deemed-complete date when set, else the last "Paid Fees"
 *     logged on or after the last submittal (Matt: the clock starts when the last invoices were
 *     paid), else the last submittal;
 *   published time: the agency Organization's "Review Turnaround In Days";
 *   comments: the first comments / corrections / approval logged on or after that date;
 *   follow-ups: status checks logged from that date until comments came back (or today).
 * See docs/PERMIT_REPORT_CARD.md.
 */
import { apiGetAll, apiGetOne } from './_api.js'
import { loadReviewClocks, cachedClock } from './reviewClock.js'
import { calDays, todayIso } from './planCheckPerformance.js'

const TBL_REVIEWS  = 'tbl2Yu7YtDZ6tFFhI'   // Agency Approvals
const TBL_ACTIVITY = 'tblLO4mxHR04VCk5v'   // Permit Activity
const TBL_ORGS     = 'tblsPUYYYOTNkiXUt'   // Organizations (agencies)

const SUBMIT   = /Submitted to Agency|Resubmitted to Agency|Submitted Permit Revision|Resubmitted Permit Revision/
const PAID     = /Paid Fees/
const COMMENTS = /Received Agency Comments|Received Agency Corrections|Received Agency Approval|Agency Approved Permit Revision/
const CHECK    = /Status Update/

const first = v => Array.isArray(v) ? v[0] : v

/** The card's facts for one project: { projectName, asOf, rows: [...] }. */
export async function getProjectReportCard(projectId) {
  const today = todayIso()
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({Project Record UID (from Project) (from Permits)},","))'
  const reviews = await apiGetAll(TBL_REVIEWS, 'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=200', null, { strict: true })
  const open = reviews.filter(r => {
    const f = r.fields || {}
    return first(f['Status']) !== 'Approved' && !f['Date Approved'] && (f['Permit Activity'] || []).length
  })
  const projectName = reviews.length ? String(first(reviews[0].fields['Project Name and Sub Name (from Permits)']) || '').trim() : ''
  if (!open.length) return { projectName, asOf: today, rows: [] }

  // Activity by the reviews' own links (real record ids), 50 to a request.
  const actIds = open.flatMap(r => r.fields['Permit Activity'] || [])
  const chunks = []
  for (let i = 0; i < actIds.length; i += 50) chunks.push(actIds.slice(i, i + 50))
  const [actPages, orgs] = await Promise.all([
    Promise.all(chunks.map(ids => apiGetAll(TBL_ACTIVITY, 'filterByFormula=' + encodeURIComponent('OR(' + ids.map(id => "RECORD_ID()='" + id + "'").join(',') + ')') + '&pageSize=100', null, { strict: true }))),
    Promise.all([...new Set(open.map(r => first(r.fields['Agency'])).filter(Boolean))].map(id => apiGetOne(TBL_ORGS, id))),
    loadReviewClocks(open.map(r => r.id)).catch(() => null),
  ])
  const acts = actPages.flat()
  const turnaround = new Map(orgs.filter(Boolean).map(o => [o.id, Number(o.fields['Review Turnaround In Days']) || null]))

  const rows = open.map(r => {
    const f = r.fields || {}
    const list = acts.filter(a => (a.fields['Agency Approval'] || []).includes(r.id) && a.fields['Date'])
      .map(a => ({ date: a.fields['Date'], type: a.fields['Activity Type'] || '', created: a.createdTime || '' }))
      .sort((x, y) => x.date.localeCompare(y.date) || x.created.localeCompare(y.created))
    const submits = list.filter(a => SUBMIT.test(a.type))
    if (!submits.length) return null
    const firstSubmitted = submits[0].date
    const lastSubmitted = submits[submits.length - 1].date
    const clock = cachedClock(r.id)
    const paid = list.filter(a => PAID.test(a.type) && a.date >= lastSubmitted).map(a => a.date).sort().pop() || null
    const start = (clock && clock.deemedCompleteOn) || paid || lastSubmitted
    const startKind = (clock && clock.deemedCompleteOn) || paid ? 'fees' : 'submitted'
    const back = list.find(a => a.date >= start && COMMENTS.test(a.type))
    const end = back ? back.date : today
    const days = calDays(start, end)
    const published = turnaround.get(first(f['Agency'])) || null
    return {
      id: r.id,
      agency: first(f['Agency Text']) || f['Name'] || 'Agency review',
      trackingNumber: f['Agency Tracking Number'] || '',
      firstSubmitted, lastSubmitted, resubmitted: submits.length > 1,
      start, startKind,
      published,
      days,
      over: published ? days - published : null,
      commentsOn: back ? back.date : null,
      checks: list.filter(a => CHECK.test(a.type) && a.date >= start && a.date <= end).length,
    }
  }).filter(Boolean).sort((a, b) => a.start.localeCompare(b.start))

  return { projectName, asOf: today, rows }
}

const fmt = ymd => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd || '')
  if (!m) return ''
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
const esc = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
const plural = (n, w) => n + ' ' + w + (n === 1 ? '' : 's')

/** The plain lines for one review, shared by the HTML and text versions. */
function lines(r) {
  const out = []
  out.push(['Submitted', fmt(r.firstSubmitted) + (r.resubmitted ? ', resubmitted ' + fmt(r.lastSubmitted) : '')])
  if (r.startKind === 'fees') out.push(['Plan check fees paid', fmt(r.start)])
  if (r.published) out.push(['Published review time', plural(r.published, 'day')])
  if (r.commentsOn) {
    out.push(['Comments received', fmt(r.commentsOn) + ', after ' + plural(r.days, 'day')])
  } else {
    let v = plural(r.days, 'day') + ' so far'
    if (r.over > 0) v += ', ' + plural(r.over, 'day') + ' past the published time'
    else if (r.over != null) v += ', ' + plural(-r.over, 'day') + ' left'
    out.push(['In review', v])
    out.push(['Comments received', 'Not yet'])
  }
  out.push(['Status checks since', String(r.checks)])
  return out
}

/** Email-ready card: { html, text }. Inline styles only, so it pastes cleanly into any mail app. */
export function renderReportCard(card) {
  const head = 'Plan check status: ' + (card.projectName || 'this project')
  const sub = 'As of ' + fmt(card.asOf) + '. Calendar days, from our permit log.'
  const textParts = [head, sub]
  let html = '<table cellpadding="0" cellspacing="0" style="border-collapse:collapse;font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#222;max-width:560px;border:1px solid #d5dadc">'
    + '<tr><td colspan="2" style="padding:10px 12px 2px;font-weight:bold;font-size:14px;color:#07272D">' + esc(head) + '</td></tr>'
    + '<tr><td colspan="2" style="padding:0 12px 8px;color:#6b7478;font-size:12px">' + esc(sub) + '</td></tr>'
  for (const r of card.rows) {
    const title = r.agency + (r.trackingNumber ? ' (' + r.trackingNumber + ')' : '')
    const late = !r.commentsOn && r.over > 0
    html += '<tr><td colspan="2" style="padding:8px 12px 4px;border-top:1px solid #d5dadc;font-weight:bold">' + esc(title) + '</td></tr>'
    textParts.push('', title)
    for (const [k, v] of lines(r)) {
      const strong = late && k === 'In review'
      html += '<tr><td style="padding:2px 12px;color:#6b7478;white-space:nowrap;vertical-align:top">' + esc(k) + '</td>'
        + '<td style="padding:2px 12px 2px 0;' + (strong ? 'font-weight:bold;color:#9b2c1f' : '') + '">' + esc(v) + '</td></tr>'
      textParts.push(k + ': ' + v)
    }
  }
  html += '<tr><td colspan="2" style="padding:6px 12px 10px"></td></tr></table>'
  return { html, text: textParts.join('\n') }
}

/** Put the card on the clipboard as rich text (with a plain-text fallback). */
export async function copyReportCard(card) {
  const { html, text } = renderReportCard(card)
  if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
    await navigator.clipboard.write([new ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([text], { type: 'text/plain' }),
    })])
  } else {
    await navigator.clipboard.writeText(text)
  }
}
